export type AdmissionInquiryStatus =
  | "pending"
  | "approved"
  | "rejected"
  | string;

export interface AdmissionInquiryFamilyMember {
  id?: number;
  relationship: string | number;
  relationship_name?: string;
  relationship_code?: string;
  name: string;
  mobile: string;
  details?: string;
}

export interface AdmissionInquiryFamilyMemberPayload {
  relationship: string;
  name: string;
  mobile: string;
  details?: string;
}

export interface AdmissionInquiryMedicine {
  id?: number;
  current_treatment_name?: string;
  name: string;
  dosage?: string;
  frequency?: string;
}

export interface AdmissionInquiryMedicinePayload {
  current_treatment_name?: string;
  name: string;
  dosage?: string;
  frequency?: string;
}

export interface AdmissionInquiryOperation {
  id?: number;
  operation_type?: string;
  operation_date?: string | null;
  operation_doctor_name?: string;
  operation_hospital_name?: string;
  district?: number;
  city?: number;
  area?: number;
  district_name?: string;
  city_name?: string;
  area_name?: string;
  operation_details?: string;
  takes_recovery_medicine: boolean;
  recovery_medicine_details?: string;
}

export interface AdmissionInquiryOperationPayload {
  operation_type?: string;
  operation_date?: string | null;
  operation_doctor_name?: string;
  operation_hospital_name?: string;
  district?: number;
  city?: number;
  area?: number;
  operation_details?: string;
  takes_recovery_medicine: boolean;
  recovery_medicine_details?: string;
}

export interface AdmissionInquiryPayload {
  application_date: string;
  inquirer_name: string;
  inquirer_mobile: string;
  inquirer_email: string;
  reference_person_name?: string;
  reference_person_mobile?: string;
  district: number;
  city: number;
  area: number;
  pincode: string;
  landmark?: string;
  elder_name: string;
  elder_weight?: number | string;
  elder_district: number;
  elder_city: number;
  elder_area: number;
  elder_pincode: string;
  elder_landmark?: string;
  reason_for_admission: string;
  other_financier_name?: string;
  other_financier_number?: string;
  other_financier_email?: string;
  pension_amount?: string;
  other_income?: string;
  additional_notes?: string;
  physical_health_status?: string;
  has_physical_disability: boolean;
  physical_disability_details?: string;
  has_mental_health_issue: boolean;
  mental_health_issue_details?: string;
  current_doctor_name?: string;
  current_hospital_name?: string;
  current_hospital_address?: string;
  currently_under_treatment: boolean;
  takes_current_medicines: boolean;
  medicines: AdmissionInquiryMedicinePayload[];
  had_operation_before: boolean;
  operations: AdmissionInquiryOperationPayload[];
  has_existing_disease: boolean;
  disease_details?: string;
  has_food_allergy: boolean;
  food_allergy_details?: string;
  has_medicine_allergy: boolean;
  medicine_allergy_details?: string;
  special_medical_instructions?: string;
  family_members: AdmissionInquiryFamilyMemberPayload[];
}

export interface AdmissionInquiryRecord extends AdmissionInquiryPayload {
  id: number;
  status: AdmissionInquiryStatus;
  district_name?: string;
  city_name?: string;
  area_name?: string;
  elder_district_name?: string;
  elder_city_name?: string;
  elder_area_name?: string;
  medicines: AdmissionInquiryMedicine[];
  operations: AdmissionInquiryOperation[];
  family_members: AdmissionInquiryFamilyMember[];
  created_at?: string;
  updated_at?: string;
}

export type AdmissionInquiry = Pick<
  AdmissionInquiryRecord,
  "id" | "inquirer_name" | "elder_name" | "created_at"
>;

export interface AdmissionInquiryStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

export function formatInquiryNumber(
  record: Pick<AdmissionInquiryRecord, "id" | "created_at">
): string {
  const year = record.created_at
    ? new Date(record.created_at).getFullYear()
    : new Date().getFullYear();
  return `INQ-${year}-${String(record.id).padStart(4, "0")}`;
}

export function normalizeInquiryStatus(
  status: AdmissionInquiryStatus
): "pending" | "approved" | "rejected" {
  const value = String(status).toLowerCase();
  if (value === "approved") {
    return "approved";
  }
  if (value === "rejected") {
    return "rejected";
  }
  return "pending";
}
